import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";
import MailOutline from "@material-ui/icons/MailOutline";
import { Redirect, useParams } from "react-router-dom";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import { bookingApi } from "functions/apis";
import { formatDateTime } from "functions/Helper";
import { HtmlPreview, announcementStatus, fromUtc } from "./AnnouncementShared";

const useStyles = makeStyles(styles);

// One read-only page for both View and Delete; Delete adds the confirmation.
const AnnouncementDetail = ({ history, isDelete }) => {
  const classes = useStyles();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [redirect, setRedirect] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const signal = useRef(new AbortController());

  useEffect(() => {
    bookingApi
      .get("Announcements/" + id, { signal: signal.current.signal })
      .then(setData)
      .catch(error => console.log(error));
    return () => signal.current.abort();
  }, []);

  const handleDelete = () => {
    setDisabled(true);
    bookingApi
      .delete("Announcements/" + id, { signal: signal.current.signal })
      .then(() => setRedirect(true))
      .catch(error => {
        if (signal.current.signal.aborted) return;
        console.log(error);
        setDisabled(false);
      });
  };

  const field = (label, value) => (
    <CustomInput
      labelText={label}
      formControlProps={{ fullWidth: true }}
      inputProps={{
        value: value ?? "",
        disabled: true,
        multiline: true
      }}
    />
  );

  if (redirect) return <Redirect to={"/admin/Announcement/List"} />;

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              {isDelete ? "DELETE ANNOUNCEMENT" : "VIEW ANNOUNCEMENT"}
            </h4>
          </CardHeader>
          <CardBody>
            {isDelete && (
              <p>Delete this announcement? This cannot be undone.</p>
            )}
            <Button onClick={() => history.goBack()} value="Back">
              {isDelete ? "CANCEL" : "BACK"}
            </Button>
            {isDelete && (
              <Button
                disabled={disabled || data === null}
                onClick={handleDelete}
                color="danger"
                value="Delete"
              >
                DELETE
              </Button>
            )}
            {data && (
              <div>
                {field("Title", data.title)}
                {field("Slug", data.slug)}
                {field("Subtitle", data.subtitle)}
                {field("Summary", data.summary)}
                {field("Banner URL", data.banner)}
                {data.banner && (
                  <img
                    src={data.banner}
                    alt="Banner"
                    style={{ maxWidth: 360, maxHeight: 180, borderRadius: 6 }}
                  />
                )}
                {field(
                  "Publish Date",
                  formatDateTime(fromUtc(data.publishDate))
                )}
                {field(
                  "Expiry Date",
                  data.expiryDate
                    ? formatDateTime(fromUtc(data.expiryDate))
                    : "No expiry"
                )}
                {field("Status", announcementStatus(data))}
                {field("Created At", formatDateTime(fromUtc(data.createdAt)))}
                {field(
                  "Updated At",
                  data.updatedAt ? formatDateTime(fromUtc(data.updatedAt)) : "-"
                )}
                <p className={classes.label}>Content</p>
                <HtmlPreview html={data.contentHtml} />
              </div>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default AnnouncementDetail;

AnnouncementDetail.propTypes = {
  history: PropTypes.object,
  isDelete: PropTypes.bool
};

export const DeleteAnnouncement = props => (
  <AnnouncementDetail {...props} isDelete />
);
